import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import MultiSelectDropdown from 'components/ReusableComponents/MultiSelectDropdown'
import Toggle from 'components/ReusableComponents/Toggle/index'
import { hotelStatusNamesFilterable } from 'main/hotelStatuses'
import { parentChainNames } from 'main/hotelBrandsAndChains'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import userApi from 'services/api/user'

import TextInput from '../../../../components/ReusableComponents/ManageHotel/TextInput'
import HealthCheckSearchableSelect from '../../Common/HealthCheckSearchableSelect'
import { ReactComponent as ExcelIcon } from '../excel.svg'
import styles from './BillingFilters.module.scss'

let csmDataLoadedForSession = false
let globalCsmDataCache = null

const BillingFilters = ({
  state,
  dispatch,
  allhotelGroups,
  allhotelChains,
  groupByHotelGroup,
  onGroupByHotelGroupChange,
  pageSize,
  onPageSizeChange,
  onDownloadCsv
}) => {
  const {
    searchString,
    chains,
    brands,
    hotelGroups,
    subsidiaryStatus,
    csmIds,
    showAlert,
    showWarning,
    showGreen,
    agingGreaterThanZero
  } = state

  const csmUserId = useSelector(s => s.login.csmUserId)
  const [csmData, setCsmData] = useState([])
  const [searchDraft, setSearchDraft] = useState(searchString || '')
  const searchInputRef = useRef(null)
  const searchDebounceTimerRef = useRef(null)
  const csmDefaultAppliedRef = useRef(false)

  const handleSearchChange = useCallback(
    e => {
      const nextValue = e.target.value
      setSearchDraft(nextValue)
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
      }
      searchDebounceTimerRef.current = setTimeout(() => {
        dispatch({ type: 'searchString', payload: nextValue })
      }, 250)
    },
    [dispatch]
  )

  useEffect(() => {
    setSearchDraft(searchString || '')
  }, [searchString])

  useEffect(() => {
    return () => {
      if (searchDebounceTimerRef.current) {
        clearTimeout(searchDebounceTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (globalCsmDataCache) {
      setCsmData(globalCsmDataCache)
    } else if (!csmDataLoadedForSession) {
      csmDataLoadedForSession = true
      userApi
        .getCustomerSuccessManagers()
        .then(data => {
          if (Array.isArray(data)) {
            globalCsmDataCache = data
            setCsmData(data)
          } else {
            setCsmData([])
          }
        })
        .catch(() => {
          setCsmData([])
          csmDataLoadedForSession = false
        })
    }
  }, [])

  useEffect(() => {
    if (!csmUserId || csmDefaultAppliedRef.current) {
      return
    }
    csmDefaultAppliedRef.current = true
    if (!csmIds || csmIds.length === 0) {
      dispatch({
        type: 'csmIds',
        payload: [`${csmUserId}`]
      })
    }
  }, [dispatch, csmUserId, csmIds])

  return (
    <div className={styles.container}>
      <div className={styles.filterGrid}>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Hotel group</span>
          <div className={styles.fieldControl}>
            <HealthCheckSearchableSelect
              value={hotelGroups}
              onChange={v => dispatch({ type: 'hotelGroups', payload: v })}
              options={{
                all: 'All hotel groups',
                ...allhotelGroups
              }}
            />
          </div>
        </div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Brand</span>
          <div className={styles.fieldControl}>
            <HealthCheckSearchableSelect
              value={brands}
              onChange={v => dispatch({ type: 'brands', payload: v })}
              options={{
                all: 'All brands',
                ...parentChainNames
              }}
            />
          </div>
        </div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Chain</span>
          <div className={styles.fieldControl}>
            <HealthCheckSearchableSelect
              value={chains}
              onChange={v => dispatch({ type: 'chains', payload: v })}
              options={{
                all: 'All chains',
                ...allhotelChains
              }}
            />
          </div>
        </div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Status</span>
          <div className={styles.fieldControl}>
            <MultiSelectDropdown
              className={styles.statusDropdown}
              options={hotelStatusNamesFilterable}
              value={subsidiaryStatus}
              hasUnselectButton
              searchable
              onChangeValue={statuses =>
                dispatch({ type: 'subsidiaryStatus', payload: statuses })
              }
              itemName="statuses"
            />
          </div>
        </div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>CSM</span>
          <div className={styles.fieldControl}>
            <MultiSelectDropdown
              className={styles.statusDropdown}
              options={Object.fromEntries(csmData.map(csm => [csm.id, csm.fullName]))}
              value={csmIds}
              hasUnselectButton
              searchable
              onChangeValue={value => dispatch({ type: 'csmIds', payload: value })}
              itemName="CSM"
              itemNamePlural="CSMs"
            />
          </div>
        </div>
        <div className={styles.fieldGroup}>
          <span className={styles.fieldLabel}>Search</span>
          <div className={styles.fieldControl}>
            <TextInput
              type="search"
              className={styles.searchInput}
              value={searchDraft}
              onChange={handleSearchChange}
              forwardedRef={searchInputRef}
              placeholder="Hotel name, ID, or invoice"
            />
          </div>
        </div>
      </div>

      <div className={styles.actionsGrid}>
        <div className={styles.actionCard}>
          <span className={styles.actionCardLabel}>View options</span>
          <div className={styles.actionCardBody}>
            <div className={styles.toggleRow}>
              <Toggle
                checked={groupByHotelGroup}
                handleChange={e => onGroupByHotelGroupChange(e.target.checked)}
                label="Group by hotel group"
                className={styles.toggle}
              />
              <Toggle
                checked={agingGreaterThanZero}
                handleChange={e =>
                  dispatch({ type: 'agingGreaterThanZero', payload: e.target.checked })
                }
                label="Aging > 0 only"
                className={styles.toggle}
              />
            </div>
            <label className={styles.pageSizeControl}>
              <span>Records per page</span>
              <select value={pageSize} onChange={e => onPageSizeChange(e.target.value)}>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="all">All</option>
              </select>
            </label>
          </div>
        </div>
        <div className={styles.actionCard}>
          <span className={styles.actionCardLabel}>Row status</span>
          <div className={styles.actionCardBody}>
            <div className={styles.toggleRow}>
              <Toggle
                className={styles.toggle}
                checked={showAlert}
                handleChange={e => dispatch({ type: 'showAlert', payload: e.target.checked })}
                label="Alert"
              />
              <Toggle
                className={styles.toggle}
                checked={showWarning}
                handleChange={e => dispatch({ type: 'showWarning', payload: e.target.checked })}
                label="Warning"
              />
              <Toggle
                className={styles.toggle}
                checked={showGreen}
                handleChange={e => dispatch({ type: 'showGreen', payload: e.target.checked })}
                label="Green"
              />
            </div>
          </div>
        </div>
        <div className={styles.actionCard}>
          <span className={styles.actionCardLabel}>Export</span>
          <div className={styles.actionCardBody}>
            <SecondaryRegularButton className={styles.downloadButton} onClick={onDownloadCsv}>
              <ExcelIcon /> Download CSV
            </SecondaryRegularButton>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BillingFilters
